// /app/frontend/src/components/admin/fabric/CollectionPanel.jsx
// Compact rectangular material cards, inline color expansion, drag-reorder colors, viz coverage bar

import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import {
  Plus, Pencil, Trash2, GripVertical, Palette, ChevronRight, ChevronDown, Replace, Wand2,
  FileSpreadsheet, DollarSign, Percent, Search, Image as ImageIcon, Sparkles, Layers, Grid, List,
  ExternalLink, Shield, RotateCcw, Link2, Columns, Merge, Scissors
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { ColorImage } from './ColorImage';
import { tierLabels } from '../../../lib/fabricLabels';
import { StockDot } from '../../fabric/StockDot';
import { InlineName } from './InlineName';
import { StockPicker } from './StockPicker';

const CollectionPanel = ({
  selectedCollection,
  selectedMaterial,
  setSelectedMaterial,
  filteredMaterials,
  materialSearch,
  setMaterialSearch,
  onEditCollection,
  onGenerateRooms,
  onDeleteCollection,
  onOpenBulkPrice,
  onOpenBulkCsv,
  onAddMaterial,
  onEditMaterial,
  onDeleteMaterial,
  mergeIds = [],
  onToggleMerge,
  onClearMerge,
  onOpenMerge,
  onBulkStock,
  onSplit,
  onMaterialReorder,
  onOpenMaterialImport,
  onAddColor,
  onEditColor,
  onDeleteColor,
  onColorReorder,
  onMaterialColorReorder,
  onOpenCsvImport,
  onOpenImagePreview,
  onReplaceTexture,
  onBulkMatch,
  onAuditColors,
  onSaveMaterialCatalogUrl,
  onResetColorOrder,
  onResyncFromCatalog,
  onCompareColors,
  brandName,
  token,
  onRename
}) => {
  const L = tierLabels(brandName);
  const [viewMode, setViewMode] = useState('grid');

  // Compute visualization coverage (must be before any conditional return)
  const vizStats = useMemo(() => {
    if (!selectedCollection) return { total: 0, withViz: 0, pct: 0 };
    let total = 0, withViz = 0;
    const materials = selectedCollection.materials || [];
    const legacyColors = selectedCollection.colors || [];
    for (const mat of materials) {
      for (const c of (mat.colors || [])) {
        total++;
        if (c.room_visualization_url || c.ai_visualization_url || (c.room_visualizations && Object.keys(c.room_visualizations).length > 0)) withViz++;
      }
    }
    for (const c of legacyColors) {
      total++;
      if (c.room_visualization_url || c.ai_visualization_url || (c.room_visualizations && Object.keys(c.room_visualizations).length > 0)) withViz++;
    }
    return { total, withViz, pct: total > 0 ? Math.round((withViz / total) * 100) : 0 };
  }, [selectedCollection]);

  if (!selectedCollection) {
    return (
      <div className="col-span-8 bg-white rounded-xl border border-neutral-200 overflow-hidden">
        <div className="h-full flex items-center justify-center text-neutral-400 py-20">
          <div className="text-center">
            <Palette className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">Select a collection</p>
            <p className="text-sm mt-1">to view and manage its contents</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="col-span-8 bg-white rounded-xl border border-neutral-200 overflow-hidden" data-testid="collection-panel">
      {/* Collection Header */}
      <div className="px-4 py-3 border-b border-neutral-200 bg-neutral-50">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-neutral-900 flex items-center gap-2">
              <InlineName
                value={selectedCollection.name}
                onSave={(name) => onRename('collection', { collection_id: selectedCollection.id }, name)}
                inputClassName="text-lg font-semibold"
                testId="collection-header-name"
              />
              <StockDot status={selectedCollection.stock_status} discontinued={!!selectedCollection.discontinued} size="lg" testId="collection-stock-badge" side="bottom" />
            </h3>
            {selectedCollection.description && (
              <p className="text-xs text-neutral-500 mt-0.5 line-clamp-1">{selectedCollection.description}</p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px] text-neutral-500">
              {selectedCollection.fabric_type && (
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Type: <strong className="text-neutral-700">{selectedCollection.fabric_type}</strong></span>
              )}
              {selectedCollection.openness && (
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" />Openness: <strong className="text-neutral-700">{selectedCollection.openness}</strong></span>
              )}
              {selectedCollection.widths?.length > 0 && (
                <span>Widths: <strong className="text-neutral-700">{selectedCollection.widths.join(', ')}</strong></span>
              )}
              {selectedCollection.catalog_url && (
                <a href={selectedCollection.catalog_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-900 underline" data-testid="catalog-url-link">
                  <ImageIcon className="w-3 h-3" /> Original Catalog
                </a>
              )}
            </div>
          </div>
          {/* Grouped action buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {onGenerateRooms && (
              <Button variant="outline" size="sm" onClick={() => onGenerateRooms(selectedCollection)} className="gap-1 border-amber-300 text-amber-800 hover:bg-amber-50 h-7 text-xs" data-testid="generate-all-rooms-btn">
                <Sparkles className="w-3 h-3" /> Generate
              </Button>
            )}
            {onBulkMatch && (
              <Button variant="outline" size="sm" onClick={onBulkMatch} className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50 h-7 text-xs" data-testid="bulk-match-btn">
                <Wand2 className="w-3 h-3" /> Match
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onOpenBulkPrice} className="gap-1 h-7 text-xs"><Percent className="w-3 h-3" /> Prices</Button>
            {onAuditColors && (
              <Button variant="outline" size="sm" onClick={() => onAuditColors(selectedCollection)} className="gap-1 border-sky-300 text-sky-700 hover:bg-sky-50 h-7 text-xs" data-testid="audit-colors-btn">
                <Shield className="w-3 h-3" /> Audit
              </Button>
            )}
            {onCompareColors && (
              <Button variant="outline" size="sm" onClick={onCompareColors} className="gap-1 border-sky-300 text-sky-700 hover:bg-sky-50 h-7 text-xs" data-testid="compare-colors-btn" title="Stored swatch vs original CDN image">
                <Columns className="w-3 h-3" /> Compare
              </Button>
            )}
            {onResyncFromCatalog && selectedCollection.catalog_url && (
              <Button variant="outline" size="sm" onClick={() => onResyncFromCatalog(selectedCollection)} className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-7 text-xs" data-testid="resync-catalog-btn">
                <RotateCcw className="w-3 h-3" /> Resync
              </Button>
            )}
            {onOpenBulkCsv && (
              <Button variant="outline" size="sm" onClick={onOpenBulkCsv} className="gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-7 text-xs" data-testid="bulk-csv-btn">
                <FileSpreadsheet className="w-3 h-3" /> CSV
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => onEditCollection(selectedCollection)} className="h-7" data-testid={`edit-collection-${selectedCollection.id}`}><Pencil className="w-3 h-3" /></Button>
            <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 h-7" onClick={() => onDeleteCollection(selectedCollection)} data-testid={`delete-collection-${selectedCollection.id}`}><Trash2 className="w-3 h-3" /></Button>
          </div>
        </div>

        {/* Visualization Coverage Bar */}
        {vizStats.total > 0 && (
          <div className="mt-2.5 flex items-center gap-3" data-testid="viz-coverage-bar">
            <span className="text-[10px] text-neutral-500 whitespace-nowrap">AI Coverage</span>
            <div className="flex-1 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${vizStats.pct >= 80 ? 'bg-emerald-500' : vizStats.pct >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                style={{ width: `${vizStats.pct}%` }}
              />
            </div>
            <span className="text-[10px] font-semibold text-neutral-700 whitespace-nowrap" data-testid="viz-coverage-label">
              {vizStats.withViz}/{vizStats.total} ({vizStats.pct}%)
            </span>
            {vizStats.pct < 100 && onGenerateRooms && (
              <button
                onClick={() => onGenerateRooms(selectedCollection)}
                className="text-[10px] text-amber-700 hover:text-amber-900 underline whitespace-nowrap"
                data-testid="viz-generate-missing-link"
              >
                Generate missing
              </button>
            )}
          </div>
        )}
      </div>

      {/* Materials Section (4-tier) or Legacy Colors (3-tier DW) */}
      {selectedCollection.materials?.length > 0 ? (
        <div className="p-4">
          {/* Materials Header */}
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-neutral-700 text-sm">{L.tier3Plural} ({selectedCollection.materials?.length || 0})</h4>
            <div className="flex gap-1.5 items-center">
              <div className="flex rounded-md border border-neutral-200 overflow-hidden">
                <button onClick={() => setViewMode('grid')} className={`p-1 ${viewMode === 'grid' ? 'bg-neutral-800 text-white' : 'bg-white text-neutral-500 hover:bg-neutral-50'}`} title="Grid view"><Grid className="w-3 h-3" /></button>
                <button onClick={() => setViewMode('list')} className={`p-1 ${viewMode === 'list' ? 'bg-neutral-800 text-white' : 'bg-white text-neutral-500 hover:bg-neutral-50'}`} title="List view"><List className="w-3 h-3" /></button>
              </div>
              <Button size="sm" variant="outline" onClick={onOpenMaterialImport} className="gap-1 h-7 text-xs"><FileSpreadsheet className="w-3 h-3" /> Import</Button>
              <Button size="sm" onClick={onAddMaterial} className="gap-1 bg-amber-600 hover:bg-amber-700 h-7 text-xs"><Plus className="w-3 h-3" /> {L.tier3}</Button>
            </div>
          </div>

          {selectedCollection.materials?.length > 3 && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
              <Input value={materialSearch} onChange={(e) => setMaterialSearch(e.target.value)} placeholder={`Search ${L.tier3Plural.toLowerCase()}...`} className="pl-8 h-8 text-sm" />
            </div>
          )}

          {mergeIds.length > 0 && (
            <div className="flex items-center justify-between gap-2 mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 flex-wrap" data-testid="merge-bar">
              <span className="text-xs text-amber-800">
                <span className="font-semibold">{mergeIds.length}</span> {mergeIds.length === 1 ? L.tier3.toLowerCase() : L.tier3Plural.toLowerCase()} selected
              </span>
              <div className="flex gap-1.5 items-center flex-wrap">
                <StockPicker onPick={(s) => onBulkStock?.({ material_ids: mergeIds }, { field: 'stock', stock_status: s })} onDiscontinued={(d) => onBulkStock?.({ material_ids: mergeIds }, { field: 'discontinued', discontinued: d })} testIdPrefix="material-stock-pick" />
                <span className="w-px h-5 bg-amber-200 mx-1" />
                <Button size="sm" variant="ghost" onClick={onClearMerge} className="h-7 text-xs">Clear</Button>
                <Button size="sm" onClick={onOpenMerge} disabled={mergeIds.length < 2} className="h-7 text-xs gap-1 bg-amber-600 hover:bg-amber-700" title={mergeIds.length < 2 ? 'Select 2 or more to merge' : 'Merge into one'} data-testid="merge-open-btn"><Merge className="w-3 h-3" /> Merge</Button>
              </div>
            </div>
          )}

          {viewMode === 'grid' ? (
            <CompactMaterialGrid
              materials={filteredMaterials}
              selectedMaterial={selectedMaterial}
              setSelectedMaterial={setSelectedMaterial}
              onEditMaterial={onEditMaterial}
              onDeleteMaterial={onDeleteMaterial}
              mergeIds={mergeIds}
              onToggleMerge={onToggleMerge}
              onRename={onRename}
              onBulkStock={onBulkStock}
              onSplit={onSplit}
              brandName={brandName}
              onAddColor={onAddColor}
              onEditColor={onEditColor}
              onDeleteColor={onDeleteColor}
              onMaterialColorReorder={onMaterialColorReorder}
              onOpenCsvImport={onOpenCsvImport}
              onOpenImagePreview={onOpenImagePreview}
              onReplaceTexture={onReplaceTexture}
              onSaveMaterialCatalogUrl={onSaveMaterialCatalogUrl}
              onResetColorOrder={onResetColorOrder}
              selectedCollection={selectedCollection}
              token={token}
            />
          ) : (
            /* LIST VIEW */
            <>
              {filteredMaterials.length > 0 ? (
                <Reorder.Group axis="y" values={filteredMaterials} onReorder={onMaterialReorder} className="space-y-1.5 mb-4">
                  {filteredMaterials.map((material) => (
                    <Reorder.Item
                      key={material.id}
                      value={material}
                      className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer transition-all text-sm ${
                        selectedMaterial?.id === material.id ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-200' : 'border-neutral-200 hover:border-neutral-300 bg-white'
                      }`}
                      onClick={() => setSelectedMaterial(selectedMaterial?.id === material.id ? null : material)}
                    >
                      <GripVertical className="w-3.5 h-3.5 text-neutral-400 cursor-grab flex-shrink-0" />
                      <input
                        type="checkbox"
                        checked={mergeIds.includes(material.id)}
                        onChange={() => onToggleMerge?.(material.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-3 h-3 accent-amber-600 flex-shrink-0"
                        title="Select to merge"
                        data-testid={`material-merge-check-list-${material.id}`}
                      />
                      <div className="w-8 h-8 rounded border border-neutral-200 overflow-hidden bg-neutral-100 flex-shrink-0">
                        {material.image_url ? <ColorImage src={material.image_url} alt="" w={128} className="w-full h-full" />
                          : <div className="w-full h-full flex items-center justify-center"><Palette className="w-3.5 h-3.5 text-neutral-300" /></div>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-neutral-800 text-xs truncate flex items-center gap-1">
                          <InlineName value={material.name} onSave={(name) => onRename('material', { collection_id: selectedCollection.id, material_id: material.id }, name)} inputClassName="text-xs" testId={`material-list-name-${material.id}`} />
                          <span className="text-neutral-400 font-normal">({material.colors?.length || 0})</span>
                        </p>
                        {material.composition && <p className="text-[10px] text-amber-600 truncate">{material.composition}</p>}
                      </div>
                      <div className="flex gap-0.5 flex-shrink-0">
                        <button onClick={(e) => { e.stopPropagation(); onEditMaterial(material); }} className="p-1 hover:bg-neutral-200 rounded"><Pencil className="w-3 h-3 text-neutral-500" /></button>
                        <button onClick={(e) => { e.stopPropagation(); onDeleteMaterial(material); }} className="p-1 hover:bg-red-100 rounded"><Trash2 className="w-3 h-3 text-red-500" /></button>
                      </div>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              ) : <EmptyMaterials label={L.tier3Plural.toLowerCase()} />}
              {selectedMaterial && (
                <DraggableColorsInline
                  material={selectedMaterial}
                  selectedCollection={selectedCollection}
                  onAddColor={onAddColor}
                  onEditColor={onEditColor}
                  onDeleteColor={onDeleteColor}
                  onMaterialColorReorder={onMaterialColorReorder}
                  onOpenCsvImport={onOpenCsvImport}
                  onOpenImagePreview={onOpenImagePreview}
                  onReplaceTexture={onReplaceTexture}
                  onSaveMaterialCatalogUrl={onSaveMaterialCatalogUrl}
                  onResetColorOrder={onResetColorOrder}
                  token={token}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <LegacyColorsGrid
          selectedCollection={selectedCollection}
          onAddColor={onAddColor}
          onEditColor={onEditColor}
          onDeleteColor={onDeleteColor}
          onColorReorder={onColorReorder}
          onOpenImagePreview={onOpenImagePreview}
          onReplaceTexture={onReplaceTexture}
          onRename={onRename}
          onBulkStock={onBulkStock}
          token={token}
        />
      )}
    </div>
  );
};

const EmptyMaterials = ({ label = 'materials' }) => (
  <div className="text-center py-6 text-neutral-500 border border-dashed border-neutral-200 rounded-lg mb-4">
    <Palette className="w-8 h-8 mx-auto mb-2 opacity-30" />
    <p className="text-sm">No {label} yet</p>
  </div>
);

/* ── COMPACT MATERIAL GRID: rectangular cards, info-only, no big thumbnails ── */
const CompactMaterialGrid = ({
  materials, selectedMaterial, setSelectedMaterial,
  onEditMaterial, onDeleteMaterial, mergeIds = [], onToggleMerge, onRename, onBulkStock, onSplit, brandName, onAddColor, onEditColor, onDeleteColor,
  onMaterialColorReorder, onOpenCsvImport, onOpenImagePreview, onReplaceTexture,
  onSaveMaterialCatalogUrl, onResetColorOrder,
  selectedCollection, token
}) => {
  if (materials.length === 0) return <EmptyMaterials />;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {materials.map((material) => {
          const isSelected = selectedMaterial?.id === material.id;
          const isMerging = mergeIds.includes(material.id);
          const colorCount = material.colors?.length || 0;
          const vizCount = (material.colors || []).filter(c => c.room_visualization_url || c.ai_visualization_url || (c.room_visualizations && Object.keys(c.room_visualizations).length > 0)).length;

          return (
            <div
              key={material.id}
              className={`rounded-lg border-2 cursor-pointer transition-all group p-2.5 ${
                isMerging ? 'border-amber-400 bg-amber-50/60' : isSelected ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-200' : 'border-neutral-200 hover:border-neutral-300 bg-white hover:shadow-sm'
              }`}
              onClick={() => setSelectedMaterial(isSelected ? null : material)}
              data-testid={`material-card-${material.id}`}
            >
              {/* Top row: name + actions */}
              <div className="flex items-start justify-between gap-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <input
                    type="checkbox"
                    checked={isMerging}
                    onChange={() => onToggleMerge?.(material.id)}
                    onClick={(e) => e.stopPropagation()}
                    className={`w-3 h-3 accent-amber-600 flex-shrink-0 transition-opacity ${isMerging || mergeIds.length > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Select to merge"
                    data-testid={`material-merge-check-${material.id}`}
                  />
                  <InlineName
                    value={material.name}
                    onSave={(name) => onRename('material', { collection_id: selectedCollection.id, material_id: material.id }, name)}
                    className="font-semibold text-xs text-neutral-900 leading-tight"
                    inputClassName="text-xs font-semibold"
                    testId={`material-name-${material.id}`}
                  />
                  <StockDot status={material.stock_status || selectedCollection?.stock_status} discontinued={!!(material.discontinued || selectedCollection?.discontinued)} size="sm" testId={`material-stock-${material.id}`} side="bottom" />
                </div>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); onEditMaterial(material); }} className="w-5 h-5 rounded flex items-center justify-center hover:bg-neutral-200" title="Edit">
                    <Pencil className="w-2.5 h-2.5 text-neutral-500" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onDeleteMaterial(material); }} className="w-5 h-5 rounded flex items-center justify-center hover:bg-red-100" title="Delete">
                    <Trash2 className="w-2.5 h-2.5 text-red-500" />
                  </button>
                </div>
              </div>

              {/* Description / composition */}
              {material.composition && (
                <p className="text-[10px] text-amber-600 truncate mt-0.5" title={material.composition}>{material.composition}</p>
              )}

              {/* Color count + price */}
              <div className="flex items-center gap-2 mt-1.5 text-[10px] text-neutral-500">
                <span className="font-medium">{colorCount} colors</span>
                {vizCount > 0 && <span className="text-emerald-600">{vizCount} AI</span>}
                {material.base_price && (
                  <span className="text-green-600 flex items-center gap-0.5"><DollarSign className="w-2.5 h-2.5" />{material.base_price}</span>
                )}
                {!material.is_visible_online && <span className="px-1 py-px bg-neutral-400 text-white text-[7px] font-bold rounded">HIDDEN</span>}
              </div>

              {/* Mini swatch strip */}
              {colorCount > 0 && (
                <div className="flex gap-0.5 mt-2 flex-wrap">
                  {material.colors.slice(0, 8).map(c => (
                    <div key={c.id} className="w-5 h-5 rounded border border-neutral-200 overflow-hidden flex-shrink-0">
                      {c.image_url ? <ColorImage src={c.image_url} alt="" hex={c.hex_color} w={128} className="w-full h-full" /> : <div className="w-full h-full" style={{ backgroundColor: c.hex_color || '#e5e5e5' }} />}
                    </div>
                  ))}
                  {colorCount > 8 && <div className="w-5 h-5 rounded bg-neutral-100 border border-neutral-200 flex items-center justify-center text-[7px] text-neutral-500 font-bold">+{colorCount - 8}</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Inline Colors — expands below the grid */}
      <AnimatePresence>
        {selectedMaterial && (
          <motion.div
            key={`colors-${selectedMaterial.id}`}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <DraggableColorsInline
              material={selectedMaterial}
              selectedCollection={selectedCollection}
              onAddColor={onAddColor}
              onEditColor={onEditColor}
              onDeleteColor={onDeleteColor}
              onMaterialColorReorder={onMaterialColorReorder}
              onOpenCsvImport={onOpenCsvImport}
              onOpenImagePreview={onOpenImagePreview}
              onReplaceTexture={onReplaceTexture}
              onSaveMaterialCatalogUrl={onSaveMaterialCatalogUrl}
              onResetColorOrder={onResetColorOrder}
              onRename={onRename}
              onBulkStock={onBulkStock}
              onSplit={onSplit}
              brandName={brandName}
              token={token}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Drag-and-Drop Colors Inline ── */
const DraggableColorsInline = ({
  material, selectedCollection, onAddColor, onEditColor, onDeleteColor,
  onMaterialColorReorder, onOpenCsvImport, onOpenImagePreview, onReplaceTexture,
  onSaveMaterialCatalogUrl, onResetColorOrder, onRename, onBulkStock, onSplit, brandName, token
}) => {
  const L = tierLabels(brandName);
  const collFabricType = selectedCollection?.fabric_type || '';
  const collOpenness = selectedCollection?.openness || '';
  const matComposition = material?.composition || '';
  const colors = material.colors || [];
  const [editingCatalogUrl, setEditingCatalogUrl] = useState(false);
  const [catalogUrlValue, setCatalogUrlValue] = useState(material?.catalog_url || '');
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const inherited = material.stock_status || selectedCollection?.stock_status;
  const inheritedDisc = !!(material.discontinued || selectedCollection?.discontinued);
  const hasCustomOrder = colors.some(c => c.custom_order !== undefined && c.custom_order !== null);

  const handleReorder = useCallback((newColors) => {
    if (onMaterialColorReorder) {
      const reordered = newColors.map((c, i) => ({ ...c, order: i }));
      onMaterialColorReorder(reordered);
    }
  }, [onMaterialColorReorder]);

  return (
    <div className="border border-amber-200 rounded-xl bg-amber-50/30 p-3 mt-2" data-testid={`material-colors-${material.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="font-semibold text-neutral-800 text-sm flex items-center gap-2 flex-shrink-0">
            <Palette className="w-3.5 h-3.5 text-amber-600" />
            <InlineName
              value={material.name}
              onSave={(name) => onRename('material', { collection_id: selectedCollection.id, material_id: material.id }, name)}
              inputClassName="text-sm font-semibold"
              testId={`material-header-name-${material.id}`}
            />
            <StockDot status={inherited} discontinued={inheritedDisc} testId={`material-header-stock-${material.id}`} side="bottom" />
            <span className="text-neutral-400 font-normal text-xs">({colors.length} colors)</span>
          </h4>
          {/* Material catalog URL link */}
          {material.catalog_url && !editingCatalogUrl && (
            <a href={material.catalog_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-sky-600 hover:text-sky-800 flex items-center gap-0.5 truncate max-w-[160px]" data-testid="material-catalog-link">
              <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" /> Original Colors
            </a>
          )}
          {editingCatalogUrl ? (
            <div className="flex items-center gap-1">
              <Input
                value={catalogUrlValue}
                onChange={(e) => setCatalogUrlValue(e.target.value)}
                placeholder="Paste original colors link..."
                className="h-6 text-[10px] w-48"
                data-testid="material-catalog-url-input"
                autoFocus
              />
              <Button size="sm" className="h-6 text-[10px] px-2 bg-sky-600 hover:bg-sky-700" onClick={() => {
                if (onSaveMaterialCatalogUrl) onSaveMaterialCatalogUrl(selectedCollection.id, material.id, catalogUrlValue);
                setEditingCatalogUrl(false);
              }}>Save</Button>
              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => { setEditingCatalogUrl(false); setCatalogUrlValue(material?.catalog_url || ''); }}>Cancel</Button>
            </div>
          ) : (
            <button onClick={() => setEditingCatalogUrl(true)} className="text-[10px] text-neutral-400 hover:text-sky-600 flex items-center gap-0.5" data-testid="edit-material-catalog-url-btn" title="Set original colors link">
              <Link2 className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        <div className="flex gap-1 flex-shrink-0 items-center">
          <button type="button" onClick={() => setSel(sel.length === colors.length ? [] : colors.map(c => c.id))} className="text-[10px] text-neutral-500 hover:text-amber-700 px-1" data-testid="color-select-all-btn">
            {sel.length === colors.length && colors.length > 0 ? 'Unselect all' : 'Select all'}
          </button>
          {hasCustomOrder && onResetColorOrder && (
            <Button size="sm" variant="outline" onClick={() => onResetColorOrder(selectedCollection.id, material.id)} className="gap-1 text-[10px] h-6 px-2 border-sky-200 text-sky-700 hover:bg-sky-50" data-testid="reset-color-order-btn" title="Reset to article number order">
              <RotateCcw className="w-2.5 h-2.5" /> Reset Order
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={onOpenCsvImport} className="gap-1 text-[10px] h-6 px-2">
            <FileSpreadsheet className="w-2.5 h-2.5" /> CSV
          </Button>
          <Button size="sm" onClick={() => onAddColor(true)} className="gap-1 bg-amber-600 hover:bg-amber-700 text-[10px] h-6 px-2">
            <Plus className="w-2.5 h-2.5" /> Color
          </Button>
        </div>
      </div>

      {sel.length > 0 && (
        <div className="flex items-center justify-between gap-2 mb-2.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 flex-wrap" data-testid="color-selection-bar">
          <span className="text-xs text-amber-800"><span className="font-semibold">{sel.length}</span> {sel.length === 1 ? 'color' : 'colors'} selected</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            <StockPicker onPick={(s) => { onBulkStock?.({ color_ids: sel }, { field: 'stock', stock_status: s }); setSel([]); }} onDiscontinued={(d) => { onBulkStock?.({ color_ids: sel }, { field: 'discontinued', discontinued: d }); setSel([]); }} testIdPrefix="color-stock-pick" />
            <span className="w-px h-5 bg-amber-200 mx-1" />
            <Button
              size="sm" variant="outline"
              onClick={() => onSplit?.(material, sel)}
              disabled={sel.length >= colors.length}
              title={sel.length >= colors.length ? 'Leave at least one color behind' : `Move selected colors into a new ${L.tier3.toLowerCase()}`}
              className="h-7 text-xs gap-1 border-amber-300 text-amber-800 hover:bg-amber-100"
              data-testid="split-open-btn"
            >
              <Scissors className="w-3 h-3" /> Split into new {L.tier3.toLowerCase()}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSel([])} className="h-7 text-xs">Clear</Button>
          </div>
        </div>
      )}

      {/* Drag-and-drop color grid */}
      {colors.length > 0 ? (
        <Reorder.Group
          axis="x"
          values={colors}
          onReorder={handleReorder}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"
          style={{ listStyle: 'none' }}
        >
          {colors.map((color) => {
            const desc = matComposition || collFabricType || '';
            const opennessInfo = collOpenness ? `${collOpenness} openness` : '';
            const descLine = [desc, opennessInfo].filter(Boolean).join(' · ');

            return (
              <Reorder.Item
                key={color.id}
                value={color}
                className="bg-white rounded-lg border border-neutral-200 overflow-hidden hover:border-amber-300 hover:shadow-sm transition-all group cursor-grab active:cursor-grabbing"
                data-testid={`color-card-${color.id}`}
              >
                {/* Swatch */}
                <div
                  className="aspect-square bg-neutral-100 relative overflow-hidden"
                  onClick={() => color.image_url && onOpenImagePreview(color)}
                >
                  {color.image_url ? (
                    <ColorImage src={color.image_url} alt={color.name} hex={color.hex_color} w={256} className="w-full h-full" testId={`color-swatch-${color.id}`} />
                  ) : color.hex_color ? (
                    <div className="w-full h-full" style={{ backgroundColor: color.hex_color }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-neutral-300" /></div>
                  )}
                  {/* Badges */}
                  <div className="absolute top-1 left-1 flex gap-0.5 items-center">
                    <input
                      type="checkbox"
                      checked={sel.includes(color.id)}
                      onChange={() => toggleSel(color.id)}
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      className={`w-3.5 h-3.5 accent-amber-600 rounded shadow transition-opacity ${sel.length > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                      title="Select color"
                      data-testid={`color-select-${color.id}`}
                    />
                    {color.is_on_sale && <span className="px-1 py-px bg-red-500 text-white text-[7px] font-bold rounded">SALE</span>}
                    {!color.is_visible_online && <span className="px-1 py-px bg-neutral-500 text-white text-[7px] font-bold rounded">HIDDEN</span>}
                    {color.publitas_texture_id && <span className="px-1 py-px bg-emerald-500 text-white text-[7px] font-bold rounded">REAL</span>}
                  </div>
                  {/* Hover actions */}
                  <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical className="w-3.5 h-3.5 text-white drop-shadow" />
                    {onReplaceTexture && (
                      <button onClick={(e) => { e.stopPropagation(); onReplaceTexture(color); }} className="w-5 h-5 bg-white/90 rounded flex items-center justify-center shadow"><Replace className="w-2.5 h-2.5 text-amber-600" /></button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); onEditColor(color, true); }} className="w-5 h-5 bg-white/90 rounded flex items-center justify-center shadow" data-testid={`edit-color-${color.id}`}><Pencil className="w-2.5 h-2.5 text-neutral-600" /></button>
                    <button onClick={(e) => { e.stopPropagation(); onDeleteColor(color); }} className="w-5 h-5 bg-white/90 rounded flex items-center justify-center shadow" data-testid={`delete-color-${color.id}`}><Trash2 className="w-2.5 h-2.5 text-red-500" /></button>
                  </div>
                </div>
                {/* Info */}
                <div className="px-2 py-1.5">
                  <div className="flex items-center gap-1">
                    <InlineName
                      value={color.name}
                      onSave={(name) => onRename('color', { collection_id: selectedCollection.id, color_id: color.id }, name)}
                      className="font-medium text-[11px] text-neutral-900 flex-1"
                      inputClassName="text-[11px]"
                      testId={`color-name-${color.id}`}
                    />
                    <StockDot status={color.stock_status || inherited} discontinued={!!(color.discontinued || inheritedDisc)} size="sm" testId={`admin-color-stock-${color.id}`} />
                  </div>
                  {color.code && <p className="text-[9px] text-neutral-500 truncate">{color.code}</p>}
                  {descLine && <p className="text-[9px] text-amber-600 truncate" title={descLine}>{descLine}</p>}
                  {color.price_per_sqft && (
                    <p className="text-[9px] text-green-600 font-medium flex items-center gap-0.5"><DollarSign className="w-2 h-2" />{color.price_per_sqft.toFixed(2)}/sq ft</p>
                  )}
                </div>
              </Reorder.Item>
            );
          })}
        </Reorder.Group>
      ) : (
        <div className="text-center py-4 text-neutral-400 border border-dashed border-neutral-200 rounded-lg bg-white">
          <Palette className="w-6 h-6 mx-auto mb-1 opacity-30" />
          <p className="text-xs">No colors in {material.name}</p>
        </div>
      )}
    </div>
  );
};

/* ── Legacy Colors Grid (DW 3-tier) ── */
const LegacyColorsGrid = ({
  selectedCollection, onAddColor, onEditColor, onDeleteColor,
  onColorReorder, onOpenImagePreview, onReplaceTexture, onRename, onBulkStock, token
}) => {
  const colors = selectedCollection.colors || [];
  const [colorSearch, setColorSearch] = useState('');
  const [sel, setSel] = useState([]);
  const toggleSel = (id) => setSel(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filteredColors = colorSearch
    ? colors.filter(c => (c.name || '').toLowerCase().includes(colorSearch.toLowerCase()) || (c.code || '').toLowerCase().includes(colorSearch.toLowerCase()))
    : colors;

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-neutral-700 text-sm">Colors ({colors.length})</h4>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setSel(sel.length === colors.length ? [] : colors.map(c => c.id))} className="text-[10px] text-neutral-500 hover:text-amber-700 px-1" data-testid="legacy-color-select-all-btn">
            {sel.length === colors.length && colors.length > 0 ? 'Unselect all' : 'Select all'}
          </button>
          <Button size="sm" onClick={() => onAddColor(false)} className="gap-1 bg-amber-600 hover:bg-amber-700 h-7 text-xs"><Plus className="w-3 h-3" /> Add Color</Button>
        </div>
      </div>

      {sel.length > 0 && (
        <div className="flex items-center justify-between gap-2 mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 flex-wrap" data-testid="color-selection-bar">
          <span className="text-xs text-amber-800"><span className="font-semibold">{sel.length}</span> {sel.length === 1 ? 'color' : 'colors'} selected</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            <StockPicker onPick={(s) => { onBulkStock?.({ color_ids: sel }, { field: 'stock', stock_status: s }); setSel([]); }} onDiscontinued={(d) => { onBulkStock?.({ color_ids: sel }, { field: 'discontinued', discontinued: d }); setSel([]); }} testIdPrefix="color-stock-pick" />
            <Button size="sm" variant="ghost" onClick={() => setSel([])} className="h-7 text-xs">Clear</Button>
          </div>
        </div>
      )}

      {colors.length > 8 && (
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
          <Input value={colorSearch} onChange={e => setColorSearch(e.target.value)} placeholder="Search colors..." className="pl-8 h-8 text-sm" data-testid="legacy-color-search" />
        </div>
      )}

      {colors.length === 0 ? (
        <div className="text-center py-8 text-neutral-500 border border-dashed border-neutral-200 rounded-lg"><Palette className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No colors yet</p></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {filteredColors.map(color => (
            <div key={color.id} className="bg-white rounded-lg border border-neutral-200 overflow-hidden hover:border-amber-300 hover:shadow-sm transition-all group" data-testid={`legacy-color-${color.id}`}>
              <div className="aspect-square bg-neutral-100 relative cursor-pointer overflow-hidden" onClick={() => color.image_url && onOpenImagePreview(color)}>
                {color.image_url ? <ColorImage src={color.image_url} alt={color.name} hex={color.hex_color} w={256} className="w-full h-full" testId={`color-swatch-${color.id}`} />
                  : color.hex_color ? <div className="w-full h-full" style={{ backgroundColor: color.hex_color }} />
                  : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-neutral-300" /></div>}
                <input
                  type="checkbox"
                  checked={sel.includes(color.id)}
                  onChange={() => toggleSel(color.id)}
                  onClick={(e) => e.stopPropagation()}
                  className={`absolute top-1 left-1 w-3.5 h-3.5 accent-amber-600 rounded shadow transition-opacity ${sel.length > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                  title="Select color"
                  data-testid={`color-select-${color.id}`}
                />
                <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {onReplaceTexture && <button onClick={(e) => { e.stopPropagation(); onReplaceTexture(color); }} className="w-5 h-5 bg-white/90 rounded flex items-center justify-center shadow"><Replace className="w-2.5 h-2.5 text-amber-600" /></button>}
                  <button onClick={(e) => { e.stopPropagation(); onEditColor(color, false); }} className="w-5 h-5 bg-white/90 rounded flex items-center justify-center shadow" data-testid={`edit-color-${color.id}`}><Pencil className="w-2.5 h-2.5 text-neutral-600" /></button>
                  <button onClick={(e) => { e.stopPropagation(); onDeleteColor(color); }} className="w-5 h-5 bg-white/90 rounded flex items-center justify-center shadow" data-testid={`delete-color-${color.id}`}><Trash2 className="w-2.5 h-2.5 text-red-500" /></button>
                </div>
              </div>
              <div className="px-2 py-1.5">
                <div className="flex items-center gap-1">
                  <InlineName
                    value={color.name}
                    onSave={(name) => onRename('color', { collection_id: selectedCollection.id, color_id: color.id }, name)}
                    className="font-medium text-[11px] text-neutral-900 flex-1"
                    inputClassName="text-[11px]"
                    testId={`color-name-${color.id}`}
                  />
                  <StockDot status={color.stock_status || selectedCollection?.stock_status} discontinued={!!(color.discontinued || selectedCollection?.discontinued)} size="sm" testId={`admin-color-stock-${color.id}`} />
                </div>
                {color.code && <p className="text-[9px] text-neutral-500 truncate">{color.code}</p>}
                {color.price_per_sqft && <p className="text-[9px] text-green-600 font-medium flex items-center gap-0.5"><DollarSign className="w-2 h-2" />{color.price_per_sqft.toFixed(2)}/sq ft</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CollectionPanel;
